import React, { Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { authService, type AuthState } from '@/services/auth';
import { checkFirebaseConnection, checkProductionReadiness } from '@/config/firebase';
// Error boundary temporarily removed due to export issues
import LoadingSpinner from '@/components/common/LoadingSpinner';

// Lazy load components for code splitting
const LoginPage = React.lazy(() => import('@/pages/LoginPage'));
const DashboardLayout = React.lazy(() => import('@/components/layout/DashboardLayout'));
const DashboardPage = React.lazy(() => import('@/pages/DashboardPage'));
const AdminRequestsPage = React.lazy(() => import('@/pages/AdminRequestsPage'));
const MosqueVerificationPage = React.lazy(() => import('@/pages/MosqueVerificationPage'));
const SystemStatsPage = React.lazy(() => import('@/pages/SystemStatsPage'));
const EmailMonitoringPage = React.lazy(() => import('@/pages/EmailMonitoringPage'));
const AuditLogsPage = React.lazy(() => import('@/pages/AuditLogsPage'));

const App: React.FC = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isOperator: false,
    error: null,
  });
  const [systemHealth, setSystemHealth] = useState<{
    firebase: boolean;
    productionReady: boolean;
    issues: string[];
  }>({
    firebase: false,
    productionReady: false,
    issues: [],
  });

  // Initialize authentication and system health
  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribeAuth = authService.onAuthStateChange(setAuthState);

    // Check system health
    const checkHealth = async (): Promise<void> => {
      try {
        const firebaseStatus = await checkFirebaseConnection();
        const productionStatus = checkProductionReadiness();
        
        setSystemHealth({
          firebase: Object.values(firebaseStatus).every(Boolean),
          productionReady: productionStatus.ready,
          issues: productionStatus.issues,
        });
      } catch (error) {
        console.error('Health check failed:', error);
        setSystemHealth(prev => ({
          ...prev,
          firebase: false,
          issues: [...prev.issues, 'Firebase connection failed'],
        }));
      }
    };

    checkHealth();

    // Cleanup
    return () => {
      unsubscribeAuth();
    };
  }, []);

  // Show system health issues in development
  useEffect(() => {
    if (import.meta.env.DEV && systemHealth.issues.length > 0) {
      console.warn('🚨 System Health Issues:', systemHealth.issues);
    }
  }, [systemHealth.issues]);

  // Show loading spinner while auth is initializing
  if (authState.isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Loading muadhin Operations Dashboard...</p>
          {import.meta.env.DEV && !systemHealth.firebase && (
            <p className="mt-2 text-amber-600 text-sm">
              ⚠️ Firebase connection issues detected
            </p>
          )}
        </div>
      </div>
    );
  }

  // Show auth error if authentication failed
  if (authState.error && !authState.user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-600 text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Authentication Error</h1>
          <p className="text-gray-600 mb-6">
            {typeof authState.error === 'string' ? authState.error : (authState.error as any)?.message || 'Failed to authenticate. Please try again.'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show system error if critical systems are down
  if (!systemHealth.firebase && systemHealth.issues.length > 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">System Error</h1>
          <p className="text-gray-600 mb-4">
            Critical system components are not available:
          </p>
          <ul className="text-left text-sm text-gray-600 mb-6">
            {systemHealth.issues.map((issue, index) => (
              <li key={index} className="mb-1">• {issue}</li>
            ))}
          </ul>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Suspense
        fallback={
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        }
      >
        <Routes>
          <Route
            path="/login"
            element={
              authState.user && authState.isOperator ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <LoginPage />
              )
            }
          />
          <Route
            path="/*"
            element={
              authState.user && authState.isOperator ? (
                <DashboardLayout>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/admin-requests" element={<AdminRequestsPage />} />
                    <Route path="/mosque-verification" element={<MosqueVerificationPage />} />
                    <Route path="/system-stats" element={<SystemStatsPage />} />
                    <Route path="/email-monitoring" element={<EmailMonitoringPage />} />
                    <Route path="/audit-logs" element={<AuditLogsPage />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </Suspense>

      {import.meta.env.DEV && (
        <div className="fixed bottom-4 left-4 z-50">
          <div className="flex flex-col space-y-2">
            <div className={`px-3 py-1 rounded text-xs font-mono ${systemHealth.firebase ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              Firebase: {systemHealth.firebase ? 'Connected' : 'Disconnected'}
            </div>
            <div className={`px-3 py-1 rounded text-xs font-mono ${authState.user && authState.isOperator ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
              Auth: {authState.user && authState.isOperator ? 'Operator' : 'Guest'}
            </div>
            {!systemHealth.productionReady && (
              <div className="px-3 py-1 rounded text-xs font-mono bg-amber-100 text-amber-800">
                Dev Mode
              </div>
            )}
          </div>
        </div>
      )}

      <div id="announcements" aria-live="polite" aria-atomic="true" className="sr-only" />
    </div>
  );
};

export default App;
